import React from 'react';
import arrayFirst from 'lodash/first';
import arrayDrop from 'lodash/drop';
import classNames from 'classnames';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useParams } from 'react-router-dom';
import styles from './styles.module.scss';
import CurrentWorkspaceIndicator from './current-user-indicator';
import { ReactComponent as HamIcon } from '../../shared/icons/hamburger.svg';
import { ReactComponent as ChatIcon } from '../../shared/icons/chat-icon.svg';
import { ReactComponent as SearchIcon } from '../../shared/icons/search-icon.svg';
import { MobileMenu } from '../mobile-layout/mobile-menu';
import { AiChatSlideout } from '../ai-chat/AiChatSlideout';
import { GlobalSearchSlideout } from '../global-search/GlobalSearchSlideout';

const ContentHeader = ({ children, noMore = false }) => {
  const titleElement = arrayFirst(children);
  const otherElements = arrayDrop(children);
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isChatOpen, setIsChatOpen] = React.useState(false);
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);
  // Set when the assistant is opened from a search result, so it starts scoped to
  // that document instead of to whatever page the header happens to be on.
  const [chatTarget, setChatTarget] = React.useState(null);

  const handleAskAi = React.useCallback((item) => {
    setChatTarget({
      documentId: item.documentId,
      // A document can be saved before it is titled; quoting an empty string in the
      // assistant's greeting reads as a bug.
      documentName: item.title?.trim() || 'this document',
      dealId: item.dealId,
    });
    setIsSearchOpen(false);
    setIsChatOpen(true);
  }, []);

  const closeChat = React.useCallback(() => {
    setIsChatOpen(false);
    setChatTarget(null);
  }, []);
  const { dealId, teamId } = useParams();

  const onToggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Cmd+K / Ctrl+K keyboard shortcut
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen((prev) => !prev);
      }
      if (e.key === 'Escape' && isSearchOpen) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isSearchOpen]);

  return (
    <header className={!!titleElement ? styles.contentHeader__withSubheader : styles.contentHeader}>
      <Container fluid>
        <Row className="flex-nowrap">
          <Col md={6} lg={8} className={classNames('align-self-center', styles.headerTitle)}>
            {titleElement ?? children}
          </Col>
          <Col className={styles.indicatorContainter}>
            <button
              className={styles.searchButton}
              onClick={() => setIsSearchOpen(true)}
              title="Search (⌘K)"
              aria-label="Open Search"
            >
              <SearchIcon />
            </button>
            <button
              className={styles.chatButton}
              onClick={() => setIsChatOpen(true)}
              title="Chat with AI"
              aria-label="Open AI Chat"
            >
              <ChatIcon />
            </button>
            <span className={styles.headerDivider} />
            <div className={styles.workspaceIndicator}>
              <CurrentWorkspaceIndicator />
            </div>
          </Col>
          {!noMore && (
            <Col xs="auto" className={styles.moreContainer}>
              <button
                className={styles.mobileSearchButton}
                onClick={() => setIsSearchOpen(true)}
                title="Search"
                aria-label="Open Search"
              >
                <SearchIcon />
              </button>
              <button
                className={styles.mobileChatButton}
                onClick={() => setIsChatOpen(true)}
                title="Chat with AI"
                aria-label="Open AI Chat"
              >
                <ChatIcon />
              </button>
              <HamIcon width={42} height={42} onClick={onToggleMenu} />
            </Col>
          )}
        </Row>
      </Container>
      {otherElements ? otherElements : <></>}
      <MobileMenu isOpen={isMenuOpen} onClose={onToggleMenu} />
      <AiChatSlideout
        isOpen={isChatOpen}
        onClose={closeChat}
        documentId={chatTarget?.documentId}
        documentName={chatTarget?.documentName}
        dealId={chatTarget?.dealId ?? dealId}
        teamId={teamId}
      />
      <GlobalSearchSlideout
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        onAskAi={handleAskAi}
      />
    </header>
  );
};

export default ContentHeader;
